import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2, Rocket, Building2, MapPin, Megaphone, BarChart3, Inbox as InboxIcon, Settings,
  ChevronRight, Sparkles, Clock,
} from "lucide-react";
import { sr } from "../../lib/sr";
import BonusInventory from "../../components/smartreach/BonusInventory";

const usd = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const TONE = { amber: "sr-badge-amber", blue: "sr-badge-blue", green: "sr-badge-green", red: "sr-badge-red", gray: "sr-badge-gray" };

// One business-owner home: overview + quick links into My Business, Locations, Campaigns,
// Performance, Inbox and Settings. Reuses existing pages/endpoints (no new data model).
export default function ClientHub() {
  const nav = useNavigate();
  const [home, setHome] = useState(null);
  const [camps, setCamps] = useState([]);
  const [budget, setBudget] = useState(null);

  useEffect(() => {
    sr.home().then(setHome).catch(() => setHome({ error: true }));
    sr.campaigns().then((r) => setCamps(r.campaigns || [])).catch(() => setCamps([]));
    sr.locationsBudgetSummary().then(setBudget).catch(() => setBudget(null));
  }, []);

  if (!home) return <div style={{ textAlign: "center", padding: 60 }}><Loader2 className="sr-spin" size={26} /></div>;

  const biz = home.profile || {};
  const name = biz.name || home.tenant?.name || "your business";
  const live = (home.live_campaigns ?? camps.filter((c) => c.display_status === "Live").length) || 0;
  const pending = camps.filter((c) => c.display_status === "Pending Review").length;
  const locCount = budget?.location_count ?? (home.locations?.length || 0);
  const nba = home.next_best_action;

  // Campaign status breakdown (honest four labels).
  const statusCounts = camps.reduce((m, c) => {
    const s = c.display_status || c.status;
    m[s] = (m[s] || 0) + 1;
    return m;
  }, {});

  const sections = [
    { key: "mybusiness", to: "/app/settings", icon: Building2, label: "My Business", sub: `${biz.category || "Business profile"}${biz.website ? " · " + biz.website.replace(/^https?:\/\//, "") : ""}` },
    { key: "locations", to: "/app/locations", icon: MapPin, label: "Locations", sub: locCount ? `${locCount} location${locCount > 1 ? "s" : ""}${budget ? ` · ${usd(budget.allocated)}/${usd(budget.plan_budget)} allocated` : ""}` : "Add your first location" },
    { key: "campaigns", to: "/app/campaigns", icon: Megaphone, label: "Campaigns", sub: camps.length ? `${camps.length} campaign${camps.length > 1 ? "s" : ""} · ${live} live · ${pending} pending` : "No campaigns yet" },
    { key: "performance", to: "/app/results", icon: BarChart3, label: "Performance", sub: "Results, spend & conversions" },
    { key: "inbox", to: "/app/inbox", icon: InboxIcon, label: "Inbox", sub: "Messages, approvals & updates" },
    { key: "settings", to: "/app/settings", icon: Settings, label: "Settings", sub: "Account, billing & access" },
  ];

  return (
    <div data-testid="client-hub">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="sr-eyebrow"><Sparkles size={13} /> Your business home</div>
          <h1 style={{ fontSize: 28, color: "var(--navy)", margin: "4px 0 0" }}>{name}</h1>
          <p className="sr-muted" style={{ marginTop: 4 }}>Everything for {name} in one place — plan, locations, campaigns and results.</p>
        </div>
        <Link to="/app/easy" className="sr-btn sr-btn-primary" data-testid="hub-build-campaign"><Rocket size={16} /> Build a campaign</Link>
      </div>

      {/* Quick stats */}
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", marginTop: 18, gap: 12 }} data-testid="hub-stats">
        <StatCard label="Live campaigns" value={live} tone="green" />
        <StatCard label="Pending review" value={pending} tone="amber" icon={<Clock size={13} />} />
        <StatCard label="Locations" value={locCount} tone="blue" />
        <StatCard label="Monthly budget" value={budget?.plan_budget ? usd(budget.plan_budget) : "—"} tone="gray" />
      </div>

      {/* Next best action */}
      {nba && (
        <div className="sr-card" data-testid="hub-nba" style={{ marginTop: 14, borderLeft: "4px solid var(--blue)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="sr-eyebrow" style={{ marginBottom: 4 }}>Recommended next step</div>
            <strong style={{ color: "var(--navy)" }}>{nba.title || nba.label || nba}</strong>
            {nba.detail && <p className="sr-muted" style={{ fontSize: 13, marginTop: 2 }}>{nba.detail}</p>}
          </div>
          {nba.to && <button className="sr-btn sr-btn-ghost" onClick={() => nav(nba.to)} data-testid="hub-nba-go">Go <ChevronRight size={15} /></button>}
        </div>
      )}

      <BonusInventory compact />

      {/* Campaign status breakdown */}
      {camps.length > 0 && (
        <div className="sr-card" data-testid="hub-campaign-status" style={{ marginTop: 14 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Campaign status</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {Object.entries(statusCounts).map(([s, n]) => {
              const tone = (camps.find((c) => (c.display_status || c.status) === s) || {}).display_tone || "gray";
              return <span key={s} className={`sr-badge ${TONE[tone] || "sr-badge-gray"}`} data-testid={`hub-status-${s.replace(/\s+/g, "-").toLowerCase()}`}>{n} {s}</span>;
            })}
          </div>
        </div>
      )}

      {/* Section tiles */}
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", marginTop: 18, gap: 12 }} data-testid="hub-sections">
        {sections.map((s) => (
          <Link key={s.key} to={s.to} className="sr-card" data-testid={`hub-section-${s.key}`} style={{ textDecoration: "none", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--blue-light)", display: "grid", placeItems: "center", flexShrink: 0 }}><s.icon size={20} color="var(--blue)" /></div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: "var(--navy)", display: "flex", alignItems: "center", gap: 4 }}>{s.label} <ChevronRight size={14} color="var(--muted)" /></div>
              <div className="sr-muted" style={{ fontSize: 12, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.sub}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, icon }) {
  return (
    <div className="sr-card" style={{ padding: 16 }}>
      <div className="sr-muted" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>{icon}{label}</div>
      <div className="sr-stat-val" style={{ marginTop: 6 }}>{value}</div>
    </div>
  );
}
